import React from "react";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import ShareIcon from "@mui/icons-material/Share";
import { DocumentSecurityOptions } from "../models/DocumentExportOptions";
import { useDocumentExport } from "../hooks/useDocumentExport";
import "./DocumentActions.css";

export interface DocumentActionsProps {
  title: string;
  fileName: string;
  documentType: string;
  generateDocument: () => Promise<Blob>;
  allowDownload?: boolean;
  allowPrint?: boolean;
  allowShare?: boolean;
  shareTitle?: string;
  shareText?: string;
  shareLabel?: string;
  disabled?: boolean;
  disabledMessage?: string;
  compact?: boolean;
  security?: DocumentSecurityOptions;
}

export default function DocumentActions({
  title,
  fileName,
  documentType,
  generateDocument,
  allowDownload = true,
  allowPrint = true,
  allowShare = true,
  shareTitle,
  shareText,
  shareLabel = "Share",
  disabled = false,
  disabledMessage,
  compact = false,
  security,
}: DocumentActionsProps) {
  const { isGenerating, error, message, download, print, share } = useDocumentExport({
    fileName,
    title: shareTitle ?? title,
    shareText,
    generateDocument,
    security,
  });
  const [disabledFeedback, setDisabledFeedback] = React.useState<string | null>(null);
  const disabledCanExplain = disabled && Boolean(disabledMessage);
  const actionsDisabled = isGenerating || (disabled && !disabledCanExplain);

  React.useEffect(() => {
    if (!disabled) setDisabledFeedback(null);
  }, [disabled]);

  const runAction = (action: () => void) => {
    if (disabled) {
      setDisabledFeedback(disabledMessage ?? null);
      return;
    }

    setDisabledFeedback(null);
    action();
  };

  const unavailableClass = disabledCanExplain
    ? " document-actions__button--unavailable"
    : "";

  return (
    <div className={`document-actions${compact ? " document-actions--compact" : ""}`} data-document-type={documentType}>
      <div className="document-actions__buttons" aria-label={`${title} document actions`} role="group">
        {allowDownload && (
          <button type="button" className={`action-button secondary${unavailableClass}`} onClick={() => runAction(download)} disabled={actionsDisabled} aria-disabled={disabled || isGenerating} aria-label={`Download ${title} PDF`}>
            <DownloadIcon fontSize="small" />
            <span>{isGenerating ? "Generating..." : "Download PDF"}</span>
          </button>
        )}
        {allowPrint && (
          <button type="button" className={`action-button secondary${unavailableClass}`} onClick={() => runAction(print)} disabled={actionsDisabled} aria-disabled={disabled || isGenerating} aria-label={`Print ${title}`}>
            <PrintIcon fontSize="small" />
            <span>Print</span>
          </button>
        )}
        {allowShare && (
          <button type="button" className={`action-button secondary${unavailableClass}`} onClick={() => runAction(share)} disabled={actionsDisabled} aria-disabled={disabled || isGenerating} aria-label={`${shareLabel} ${title}`}>
            <ShareIcon fontSize="small" />
            <span>{shareLabel}</span>
          </button>
        )}
      </div>
      {error && <div className="document-actions__message document-actions__message--error" role="alert">{error}</div>}
      {disabledFeedback && <div className="document-actions__message" role="status">{disabledFeedback}</div>}
      {message && <div className="document-actions__message" role="status">{message}</div>}
    </div>
  );
}
